/**
 * Departments List Page
 * Main page for listing and managing departments
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Alert,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Warning as RiskIcon,
  Factory as SourceIcon,
  Science as ExamIcon,
  FileDownload as ExportIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as DepartmentIcon,
  LocationOn as LocationIcon,
  Restaurant as FoodHandlingIcon,
  ElectricBolt as ElectricalIcon,
  Elevator as PemtIcon,
  Compress as ConfinedSpaceIcon,
  Height as HeightWorkIcon,
  LocalFireDepartment as HotWorkIcon,
  Forklift as ForkliftIcon,
  Agriculture as SelfPropelledMachineIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

// Components
import { DepartmentFilters } from './components';
import {
  PageShell,
  DataTable,
  type DataTableColumn,
  AlertBadge,
  generateDepartmentAlerts,
  RouteActionButton,
} from '../../components';
import { pode } from '../../authorization/pode';
import { useAcao, useControle } from '../../authorization/useVeredito';

// Services and types
import { departmentService } from '../../services/departmentService';
import type { Department, DepartmentFilters as DepartmentFiltersType } from '../../types/department';
import { SAFETY_CHARACTERISTIC_LABELS, SafetyCharacteristicType } from '../../types/department';

// Hooks
import { useDepartmentExport } from '../../hooks/useDepartmentExport';

interface DepartmentFiltersData {
  searchTerm: string;
  companyIds: string[];
  safetyCharacteristics: SafetyCharacteristicType[];
}

type SafetyIcon = { icon: typeof FoodHandlingIcon; label: string; color: string };

/** Mensagem de erro vinda da API (mesmo shape usado antes com `error.response.data.message`). */
const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const message = (error as { response?: { data?: { message?: string } } } | null)?.response?.data
    ?.message;
  return message || fallback;
};

// Get safety characteristics icons
const getSafetyIcons = (department: Department): SafetyIcon[] => {
  const icons: SafetyIcon[] = [];

  department.safetyCharacteristics?.forEach((char) => {
    const characteristic = char.characteristic;
    const label = SAFETY_CHARACTERISTIC_LABELS[characteristic];

    switch (characteristic) {
      case SafetyCharacteristicType.FOOD_HANDLING:
        icons.push({ icon: FoodHandlingIcon, label, color: 'orange' });
        break;
      case SafetyCharacteristicType.ELECTRICAL:
        icons.push({ icon: ElectricalIcon, label, color: 'yellow' });
        break;
      case SafetyCharacteristicType.PEMT:
        icons.push({ icon: PemtIcon, label, color: 'blue' });
        break;
      case SafetyCharacteristicType.CONFINED_SPACE:
        icons.push({ icon: ConfinedSpaceIcon, label, color: 'red' });
        break;
      case SafetyCharacteristicType.HEIGHT_WORK:
        icons.push({ icon: HeightWorkIcon, label, color: 'purple' });
        break;
      case SafetyCharacteristicType.HOT_WORK:
        icons.push({ icon: HotWorkIcon, label, color: 'red' });
        break;
      case SafetyCharacteristicType.FORKLIFT:
        icons.push({ icon: ForkliftIcon, label, color: 'green' });
        break;
      case SafetyCharacteristicType.SELF_PROPELLED_MACHINE:
        icons.push({ icon: SelfPropelledMachineIcon, label, color: 'gray' });
        break;
    }
  });

  return icons;
};

const hasSafetyRisks = (department: Department): boolean =>
  Boolean(department.safetyCharacteristics && department.safetyCharacteristics.length > 0);

const getDepartmentAlerts = (department: Department) => {
  if (!department._counts) return [];
  return generateDepartmentAlerts({
    occupationCount: department._counts.occupations,
    employeeCount: department._counts.employees,
    riskApplicationCount: department._counts.riskApplications,
    sourceApplicationCount: department._counts.sourceApplications,
    examApplicationCount: department._counts.examApplications,
  });
};

const columns: DataTableColumn<Department>[] = [
  {
    id: 'name',
    label: 'Setor',
    primary: true,
    render: (department) => (
      <Box display="flex" alignItems="center">
        <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
          <DepartmentIcon />
        </Avatar>
        <Box>
          <Box display="flex" alignItems="center">
            <Typography variant="subtitle2" fontWeight="medium">
              {department.name}
            </Typography>
            <AlertBadge alerts={getDepartmentAlerts(department)} />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {department.unit?.name ?? '—'}
          </Typography>
        </Box>
      </Box>
    ),
  },
  {
    id: 'company',
    label: 'Empresa',
    render: (department) =>
      department.company ? (
        <Typography variant="body2">
          {department.company.tradeName || department.company.legalName}
        </Typography>
      ) : null,
  },
  {
    id: 'location',
    label: 'Localização',
    render: (department) => (
      <Box display="flex" alignItems="center">
        {department.location ? (
          <>
            <LocationIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="body2">{department.location}</Typography>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Não informado
          </Typography>
        )}
      </Box>
    ),
  },
  {
    id: 'safetyCharacteristics',
    label: 'Atividades Especiais',
    render: (department) => {
      const safetyIcons = getSafetyIcons(department);
      return (
        <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center">
          {safetyIcons.slice(0, 3).map((safety, index) => (
            <Tooltip key={index} title={safety.label}>
              <safety.icon fontSize="small" sx={{ color: safety.color }} />
            </Tooltip>
          ))}
          {safetyIcons.length > 3 && (
            <Typography variant="caption" color="text.secondary">
              +{safetyIcons.length - 3}
            </Typography>
          )}
          {hasSafetyRisks(department) ? (
            <Tooltip title="Possui riscos de segurança">
              <WarningIcon sx={{ color: 'warning.main' }} fontSize="small" />
            </Tooltip>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Nenhum risco identificado
            </Typography>
          )}
        </Box>
      );
    },
  },
];

const DepartmentsListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const editar = useControle('/departments/:id/edit', pode('department.update'), 'Editar');
  const excluir = useAcao('department.delete', 'Excluir');
  const { exporting, exportDepartments } = useDepartmentExport();

  // Get company filter from URL params if present
  const companyId = searchParams.get('companyId');

  // State
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [currentFilters, setCurrentFilters] = useState<Partial<DepartmentFiltersType>>({});
  const [searchTerm, setSearchTerm] = useState('');

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [departmentToDelete, setDepartmentToDelete] = useState<Department | null>(null);

  // Load departments from API
  const loadDepartments = React.useCallback(async (
    pageNumber: number = 1,
    limit: number = 10,
    search: string = '',
    filters: Partial<DepartmentFiltersType> = {}
  ) => {
    setLoading(true);
    setError(null);

    try {
      console.log('🔄 Loading departments from API:', { pageNumber, limit, search, filters });

      const response = await departmentService.getDepartments({
        page: pageNumber,
        limit,
        search,
        companyId: companyId ?? undefined,
        filters
      });

      setDepartments(response.data);
      setTotalCount(response.pagination.total);

      console.log('✅ Departments loaded:', response);
      // Remove success snackbar to avoid spam
    } catch (error: unknown) {
      console.error('❌ Error loading departments:', error);
      const errorMessage = getApiErrorMessage(error, 'Erro ao carregar setores');
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [companyId, enqueueSnackbar]);

  // Initial load
  useEffect(() => {
    loadDepartments(1, rowsPerPage, '', {});
  }, [companyId]); // Reload when companyId changes

  // Handle filters
  const handleFilter = React.useCallback((filtersData: DepartmentFiltersData) => {
    console.log('🔍 Applying filters:', filtersData);

    const newFilters: Partial<DepartmentFiltersType> = {};

    // Support multiple companies - send as comma-separated string
    if (filtersData.companyIds.length > 0) {
      newFilters.companyId = filtersData.companyIds.join(',');
    }

    // Send safety characteristics as array directly
    if (filtersData.safetyCharacteristics.length > 0) {
      newFilters.safetyCharacteristics = filtersData.safetyCharacteristics;
    }

    setCurrentFilters(newFilters);
    setSearchTerm(filtersData.searchTerm);
    setPage(1); // Reset to first page when filtering

    loadDepartments(1, rowsPerPage, filtersData.searchTerm, newFilters);
  }, [loadDepartments, rowsPerPage]);

  // Handle pagination
  const handlePageChange = React.useCallback((newPage: number) => {
    const newPageNumber = newPage + 1; // Material-UI uses 0-based pages, API uses 1-based
    setPage(newPageNumber);
    loadDepartments(newPageNumber, rowsPerPage, searchTerm, currentFilters);
  }, [loadDepartments, rowsPerPage, searchTerm, currentFilters]);

  const handleRowsPerPageChange = React.useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
    loadDepartments(1, newRowsPerPage, searchTerm, currentFilters);
  }, [loadDepartments, searchTerm, currentFilters]);

  // Handle actions
  const handleEdit = (department: Department) => {
    console.log('✏️ Edit department:', department.id);
    navigate(`/departments/${department.id}/edit`);
  };

  const handleDelete = React.useCallback(async (department: Department) => {
    try {
      console.log('🗑️ Deleting department:', department.id);

      // Check if can be deleted first
      const canDeleteResult = await departmentService.canDeleteDepartment(department.id);

      if (!canDeleteResult.canDelete) {
        enqueueSnackbar(
          canDeleteResult.message ?? 'Não é possível excluir este setor',
          { variant: 'warning' }
        );
        return;
      }

      await departmentService.deleteDepartment(department.id);
      enqueueSnackbar('Setor excluído com sucesso!', { variant: 'success' });

      // Reload current page
      loadDepartments(page, rowsPerPage, searchTerm, currentFilters);
    } catch (error: unknown) {
      console.error('❌ Error deleting department:', error);
      enqueueSnackbar(getApiErrorMessage(error, 'Erro ao excluir setor'), { variant: 'error' });
    }
  }, [loadDepartments, page, rowsPerPage, searchTerm, currentFilters, enqueueSnackbar]);

  const handleDeleteClick = (department: Department) => {
    setDepartmentToDelete(department);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (departmentToDelete) {
      handleDelete(departmentToDelete);
    }
    setDeleteDialogOpen(false);
    setDepartmentToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDepartmentToDelete(null);
  };

  const handleCreate = () => {
    if (companyId) {
      navigate(`/departments/create?companyId=${companyId}`);
    } else {
      navigate('/departments/create');
    }
  };

  const filtersActive = searchTerm.trim() !== '' || Object.keys(currentFilters).length > 0;

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: companyId ? 'Setores da Empresa' : 'Setores' },
      ]}
      title={companyId ? 'Setores da Empresa' : 'Setores'}
      actions={
        <>
          <RouteActionButton
            para="/risk-applications"
            exige={pode('risk_application.read', { perspectiva: 'plataforma' })}
            variant="outlined"
            color="warning"
            size="small"
            startIcon={<RiskIcon />}
          >
            Aplicar Riscos
          </RouteActionButton>
          <RouteActionButton
            para="/source-applications"
            exige={pode('source_application.read', { perspectiva: 'plataforma' })}
            variant="outlined"
            color="info"
            size="small"
            startIcon={<SourceIcon />}
          >
            Aplicar Fontes
          </RouteActionButton>
          <RouteActionButton
            para="/exam-applications"
            exige={pode('exam_application.read', { perspectiva: 'plataforma' })}
            variant="outlined"
            color="success"
            size="small"
            startIcon={<ExamIcon />}
          >
            Aplicar Exames
          </RouteActionButton>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={() => exportDepartments(currentFilters)}
            disabled={loading || exporting}
          >
            Exportar
          </Button>
          <RouteActionButton
            para="/departments/create"
            exige={pode('department.create')}
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            aoAgir={handleCreate}
          >
            Novo Setor
          </RouteActionButton>
        </>
      }
    >
      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ mb: 3 }}>
        <DepartmentFilters
          onFilter={handleFilter}
          loading={loading || exporting}
          preSelectedCompanyId={companyId ?? undefined}
          total={totalCount}
        />
      </Box>

      {/* Departments Table */}
      <DataTable
        columns={columns}
        rows={departments}
        getRowId={(department) => department.id}
        loading={loading}
        emptyState={{
          title: 'Nenhum setor cadastrado ainda',
          description:
            'Setores organizam os funcionários de uma empresa e são exigidos no cadastro de cada um.',
          prereqChain: 'departments',
          action: {
            label: 'Cadastrar setor',
            node: 'departments',
            deniedHint: 'Peça ao administrador da empresa para cadastrar um setor.',
          },
        }}
        filtersActive={filtersActive}
        actions={[
          {
            icon: <EditIcon fontSize="small" />,
            onClick: handleEdit,
            ...editar,
          },
          {
            icon: <DeleteIcon fontSize="small" />,
            color: 'error',
            onClick: handleDeleteClick,
            ...excluir,
          },
        ]}
        pagination={{
          page: page - 1,
          rowsPerPage,
          totalCount,
          onPageChange: handlePageChange,
          onRowsPerPageChange: handleRowsPerPageChange,
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o setor "{departmentToDelete?.name}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancelar</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default DepartmentsListPage;
